import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { FaPlus, FaTrash } from 'react-icons/fa';
import PasteUploadArea from '../PasteUploadArea';
import { createVerificationRecord } from '../../utils/documentVerification';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [forms, setForms] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [uploadFields, setUploadFields] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    displayInMasterData: true,
    description: '',
    logo: null,
    fees: {
      governmentFees: { name: '', amount: 0, taxRate: 0, isCustomizable: false },
      professionalFees: { name: '', amount: 0, taxRate: 0, isCustomizable: false },
      otherFees: []
    },
    requiredFields: [],
    documentRequirements: [],
    verificationTypes: [],
    associatedForms: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch forms and documents for dropdowns
        const [formsRes, docsRes, usersRes, groupsRes] = await Promise.all([
          api.get('/forms'),
          api.get('/master-data'),
          api.get('/users'),
          api.get('/groups')
        ]);
        setForms(formsRes.data.data);
        setDocuments(docsRes.data.data);
        setUsers(usersRes.data.data);
        setGroups(groupsRes.data.data);

        // If editing, fetch product data
        if (isEditMode) {
          const productRes = await api.get(`/admin/products/${id}`);
          setFormData(productRes.data);
          // Set logo preview if exists
          if (productRes.data.logo?.url) {
            setLogoPreview(productRes.data.logo.url);
          }
        }
      } catch (error) {
        toast.error('Failed to fetch required data');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup function to revoke object URLs
    return () => {
      if (logoPreview && !logoPreview.startsWith('http')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [id, isEditMode]);

  useEffect(() => {
    if (isEditMode && id) {
      fetchProduct();
    }
    fetchVerificationTypes();
  }, [isEditMode, id]);

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      console.error('Error fetching verification types:', error);
      toast.error('Failed to fetch verification types');
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/admin/products/${id}`);
      const product = response.data;
      setFormData({
        name: product.name,
        description: product.description || '',
        displayInMasterData: product.displayInMasterData,
        isActive: product.isActive,
        logo: product.logo,
        fees: {
          governmentFees: product.fees.governmentFees || { name: '', amount: 0, taxRate: 0, isCustomizable: false },
          professionalFees: product.fees.professionalFees || { name: '', amount: 0, taxRate: 0, isCustomizable: false },
          otherFees: product.fees.otherFees || []
        },
        requiredFields: product.requiredFields || [],
        documentRequirements: product.documentRequirements || [],
        verificationTypes: product.verificationTypes || [],
        associatedForms: product.associatedForms || []
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to fetch product details');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFeeChange = (feeType, field, value) => {
    setFormData((prev) => ({
      ...prev,
      fees: {
        ...prev.fees,
        [feeType]: {
          ...prev.fees[feeType],
          [field]: value
        }
      }
    }));
  };

  const handleOtherFeeAdd = () => {
    setFormData((prev) => ({
      ...prev,
      fees: {
        ...prev.fees,
        otherFees: [
          ...prev.fees.otherFees,
          { name: '', amount: 0, taxRate: 0, isCustomizable: false }
        ]
      }
    }));
  };

  const handleOtherFeeRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      fees: {
        ...prev.fees,
        otherFees: prev.fees.otherFees.filter((_, i) => i !== index)
      }
    }));
  };

  const handleRequiredFieldAdd = () => {
    setFormData((prev) => ({
      ...prev,
      requiredFields: [
        ...prev.requiredFields,
        {
          fieldType: 'text',
          label: '',
          placeholder: '',
          isRequired: true,
          supportsAutofill: false
        }
      ]
    }));
  };

  const handleRequiredFieldRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      requiredFields: prev.requiredFields.filter((_, i) => i !== index)
    }));
  };

  const handleDocumentRequirementAdd = () => {
    setFormData(prev => ({
      ...prev,
      documentRequirements: [
        ...prev.documentRequirements,
        {
          name: '',
          description: '',
          masterDataSource: null,
          template: {
            fileUrl: '',
            fileName: '',
            tokens: []
          },
          generateOnlyIfMissing: false,
          notifications: {
            onGenerate: {
              email: { enabled: false },
              sms: { enabled: false }
            }
          }
        }
      ]
    }));
  };

  const handleDocumentRequirementRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      documentRequirements: prev.documentRequirements.filter((_, i) => i !== index)
    }));
  };

  const handleLogoUpload = async (file) => {
    try {
      if (!file) return;

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }

      // Create object URL for preview
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      if (isEditMode) {
        const formData = new FormData();
        formData.append('logo', file);

        const response = await api.put(`/admin/products/${id}/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data?.logo) {
          setFormData(prev => ({
            ...prev,
            logo: response.data.logo
          }));

          // Create verification record for the uploaded logo
          try {
            const verificationData = {
              documentType: 'productTemplate',
              documentId: id,
              documentField: 'logo',
              fileUrl: response.data.logo.url,
              fileName: file.name,
              fileSize: file.size
            };

            const verificationResult = await createVerificationRecord(verificationData);
            
            if (verificationResult.verificationRequired) {
              toast.info('Logo uploaded successfully. Verification required before approval.');
            } else {
              toast.success('Logo uploaded successfully');
            }
          } catch (verificationError) {
            console.error('Verification error:', verificationError);
            toast.success('Logo uploaded successfully');
          }
        }
      } else {
        // For new products, store the file for later upload
        setFormData(prev => ({
          ...prev,
          logo: {
            file,
            previewUrl
          }
        }));
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo');
      // Clear preview on error
      setLogoPreview(null);
      setFormData(prev => ({
        ...prev,
        logo: null
      }));
    }
  };

  const handleTemplateUpload = async (index, file) => {
    try {
      if (!file) return;

      // Ensure the document requirement exists at this index
      if (index >= formData.documentRequirements.length) {
        toast.error('Please add a document requirement first');
        return;
      }

      const formData1 = new FormData();
      formData1.append('template', file);
      formData1.append('index', index);

      if (isEditMode) {
        const response = await api.post(
          `/admin/products/${id}/templates`,
          formData1,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            params: { index }
          }
        );

        if (response?.data) {
          setFormData(prev => {
            const newDocReqs = [...prev.documentRequirements];
            // Ensure the document requirement exists
            if (!newDocReqs[index]) {
              newDocReqs[index] = {
                name: '',
                description: '',
                masterDataSource: null,
                template: null,
                generateOnlyIfMissing: false,
                notifications: {
                  onGenerate: {
                    email: { enabled: false },
                    sms: { enabled: false }
                  }
                }
              };
            }
            newDocReqs[index] = {
              ...newDocReqs[index],
              template: {
                fileUrl: response.data.fileUrl,
                fileName: response.data.fileName,
                tokens: response.data.tokens || []
              }
            };
            return { ...prev, documentRequirements: newDocReqs };
          });

          // Create verification record for the uploaded template
          try {
            const verificationData = {
              documentType: 'productTemplate',
              documentId: id,
              documentField: `template_${index}`,
              fileUrl: response.data.fileUrl,
              fileName: file.name,
              fileSize: file.size
            };

            const verificationResult = await createVerificationRecord(verificationData);
            
            if (verificationResult.verificationRequired) {
              toast.info('Template uploaded successfully. Verification required before approval.');
            } else {
              toast.success('Template uploaded successfully');
            }
          } catch (verificationError) {
            console.error('Verification error:', verificationError);
            toast.success('Template uploaded successfully');
          }
        }
      } else {
        // For new products, store the file for later upload
        setFormData(prev => {
          const newDocReqs = [...prev.documentRequirements];
          // Ensure the document requirement exists
          if (!newDocReqs[index]) {
            newDocReqs[index] = {
              name: '',
              description: '',
              masterDataSource: null,
              template: null,
              generateOnlyIfMissing: false,
              notifications: {
                onGenerate: {
                  email: { enabled: false },
                  sms: { enabled: false }
                }
              }
            };
          }
          newDocReqs[index] = {
            ...newDocReqs[index],
            template: {
              file,
              fileName: file.name,
              tokens: []
            }
          };
          return { ...prev, documentRequirements: newDocReqs };
        });
        toast.success('Template will be uploaded when creating the product');
      }
    } catch (error) {
      console.error('Error uploading template:', error);
      toast.error(error.response?.data?.message || 'Failed to upload template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create a new object with properly formatted data
      const formattedData = {
        name: formData.name,
        description: formData.description,
        displayInMasterData: Boolean(formData.displayInMasterData),
        isActive: Boolean(formData.isActive),
        requiredFields: formData.requiredFields.map(field => ({
          fieldType: field.fieldType,
          label: field.label,
          placeholder: field.placeholder || '',
          options: Array.isArray(field.options) ? field.options : [],
          isRequired: Boolean(field.isRequired),
          supportsAutofill: Boolean(field.supportsAutofill),
          autofillApiEndpoint: field.autofillApiEndpoint || ''
        })),
        documentRequirements: formData.documentRequirements.map(req => ({
          name: req.name,
          description: req.description || '',
          masterDataSource: req.masterDataSource || null,
          template: {
            fileUrl: req.template?.fileUrl || '',
            fileName: req.template?.fileName || '',
            tokens: Array.isArray(req.template?.tokens) ? req.template.tokens : []
          },
          generateOnlyIfMissing: Boolean(req.generateOnlyIfMissing),
          notifications: {
            onGenerate: {
              email: {
                enabled: Boolean(req.notifications?.onGenerate?.email?.enabled)
              },
              sms: {
                enabled: Boolean(req.notifications?.onGenerate?.sms?.enabled)
              }
            }
          }
        })),
        fees: {
          governmentFees: {
            name: formData.fees.governmentFees.name || 'Government Fees',
            amount: Number(formData.fees.governmentFees.amount) || 0,
            taxRate: Number(formData.fees.governmentFees.taxRate) || 0,
            isCustomizable: Boolean(formData.fees.governmentFees.isCustomizable)
          },
          professionalFees: {
            name: formData.fees.professionalFees.name || 'Professional Fees',
            amount: Number(formData.fees.professionalFees.amount) || 0,
            taxRate: Number(formData.fees.professionalFees.taxRate) || 0,
            isCustomizable: Boolean(formData.fees.professionalFees.isCustomizable)
          },
          otherFees: formData.fees.otherFees.map(fee => ({
            name: fee.name || '',
            amount: Number(fee.amount) || 0,
            taxRate: Number(fee.taxRate) || 0,
            isCustomizable: Boolean(fee.isCustomizable)
          })),
        },
        verificationTypes: formData.verificationTypes
      };

      if (isEditMode) {
        // Update product data
        await api.put(`/admin/products/${id}`, formattedData);
        toast.success('Product updated successfully');
      } else {
        // For new products, send everything in one request
        const formDataToSend = new FormData();
        formDataToSend.append('data', JSON.stringify(formattedData));
        
        // Append logo if exists
        if (formData.logo) {
          formDataToSend.append('logo', formData.logo.file);
        }
        
        // Append templates and their indices
        const templateIndices = [];
        formData.documentRequirements.forEach((docReq, index) => {
          if (docReq.template?.file) {
            formDataToSend.append('templates', docReq.template.file);
            templateIndices.push(index);
          }
        });
        
        if (templateIndices.length > 0) {
          formDataToSend.append('templateIndices', JSON.stringify(templateIndices));
        }

        const response = await api.post('/admin/products', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data) {
        toast.success('Product created successfully');
        }
      }

      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchUploadFields = async () => {
      try {
        const response = await api.get('/admin/upload-fields');
        setUploadFields(response.data);
      } catch (error) {
        toast.error('Failed to fetch upload fields');
      }
    };
    fetchUploadFields();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {isEditMode ? 'Edit Product' : 'Add New Product'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Details</h2>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Product Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo
              </label>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <div className="relative w-32 h-32">
                      <img
                        src={logoPreview}
                        alt="Product logo preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setLogoPreview(null);
                          setFormData(prev => ({
                            ...prev,
                            logo: null
                          }));
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <PasteUploadArea
                    onFileSelect={handleLogoUpload}
                    accept="image/*"
                    maxSize={5 * 1024 * 1024}
                    placeholder="Upload a logo image (PNG, JPG up to 5MB)"
                    uploadedUrl={formData.logo?.url || logoPreview}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Upload a logo image (PNG, JPG up to 5MB)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="displayInMasterData"
                  checked={formData.displayInMasterData}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Display in Master Data
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Fee Structure */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Fee Structure</h2>
          
          {/* Government Fees */}
          <div className="mb-4">
            <h3 className="text-md font-medium mb-2">Government Fees</h3>
            <div className="grid grid-cols-2 gap-4 mb-2">
            <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                value={formData.fees.governmentFees.amount}
                onChange={(e) => handleFeeChange('governmentFees', 'amount', parseFloat(e.target.value))}
                    className="block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                min="0"
                step="0.01"
              />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    value={formData.fees.governmentFees.taxRate}
                    onChange={(e) => handleFeeChange('governmentFees', 'taxRate', parseFloat(e.target.value))}
                    className="block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center mt-6">
                <input
                  type="checkbox"
                  checked={formData.fees.governmentFees.isCustomizable}
                  onChange={(e) => handleFeeChange('governmentFees', 'isCustomizable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Customizable per user
                </span>
              </label>
              </div>
            </div>
          </div>

          {/* Professional Fees */}
          <div className="mb-4">
            <h3 className="text-md font-medium mb-2">Professional Fees</h3>
            <div className="grid grid-cols-2 gap-4 mb-2">
            <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                value={formData.fees.professionalFees.amount}
                onChange={(e) => handleFeeChange('professionalFees', 'amount', parseFloat(e.target.value))}
                    className="block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                min="0"
                step="0.01"
              />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    value={formData.fees.professionalFees.taxRate}
                    onChange={(e) => handleFeeChange('professionalFees', 'taxRate', parseFloat(e.target.value))}
                    className="block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center mt-6">
                <input
                  type="checkbox"
                  checked={formData.fees.professionalFees.isCustomizable}
                  onChange={(e) => handleFeeChange('professionalFees', 'isCustomizable', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Customizable per user
                </span>
              </label>
              </div>
            </div>
          </div>

          {/* Other Fees */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-md font-medium">Other Fees</h3>
              <button
                type="button"
                onClick={() => handleOtherFeeAdd()}
                className="text-blue-600 hover:text-blue-800"
              >
                <FaPlus className="h-5 w-5" />
              </button>
            </div>
            {formData.fees.otherFees.map((fee, index) => (
              <div key={index} className="grid grid-cols-12 gap-4 mb-2 items-start">
                <div className="col-span-3">
                  <label className="block text-sm text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={fee.name}
                  onChange={(e) => {
                    const newOtherFees = [...formData.fees.otherFees];
                    newOtherFees[index] = { ...fee, name: e.target.value };
                    setFormData(prev => ({
                      ...prev,
                      fees: { ...prev.fees, otherFees: newOtherFees }
                    }));
                  }}
                  placeholder="Fee Name"
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  value={fee.amount}
                  onChange={(e) => {
                    const newOtherFees = [...formData.fees.otherFees];
                    newOtherFees[index] = { ...fee, amount: parseFloat(e.target.value) };
                    setFormData(prev => ({
                      ...prev,
                      fees: { ...prev.fees, otherFees: newOtherFees }
                    }));
                  }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  min="0"
                  step="0.01"
                />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    value={fee.taxRate}
                    onChange={(e) => {
                      const newOtherFees = [...formData.fees.otherFees];
                      newOtherFees[index] = { ...fee, taxRate: parseFloat(e.target.value) };
                      setFormData(prev => ({
                        ...prev,
                        fees: { ...prev.fees, otherFees: newOtherFees }
                      }));
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
                <div className="col-span-4">
                  <label className="flex items-center mt-6">
                  <input
                    type="checkbox"
                    checked={fee.isCustomizable}
                    onChange={(e) => {
                      const newOtherFees = [...formData.fees.otherFees];
                      newOtherFees[index] = { ...fee, isCustomizable: e.target.checked };
                      setFormData(prev => ({
                        ...prev,
                        fees: { ...prev.fees, otherFees: newOtherFees }
                      }));
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                    <span className="ml-2 text-sm text-gray-700">Customizable</span>
                <button
                  type="button"
                  onClick={() => handleOtherFeeRemove(index)}
                      className="ml-4 text-red-600 hover:text-red-800"
                >
                  <FaTrash className="h-5 w-5" />
                </button>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Required Fields */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Required Fields</h2>
            <button
              type="button"
              onClick={handleRequiredFieldAdd}
              className="text-blue-600 hover:text-blue-800"
            >
              <FaPlus className="h-5 w-5" />
            </button>
          </div>
          
          {formData.requiredFields.map((field, index) => (
            <div key={index} className="grid grid-cols-2 gap-4 mb-4 p-4 border rounded">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Field Type
                </label>
                <select
                  value={field.fieldType}
                  onChange={(e) => {
                    const newFields = [...formData.requiredFields];
                    newFields[index] = { ...field, fieldType: e.target.value };
                    setFormData(prev => ({ ...prev, requiredFields: newFields }));
                  }}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="text">Text</option>
                  <option value="date">Date</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="file">File</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Field Label
                </label>
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => {
                    const newFields = [...formData.requiredFields];
                    newFields[index] = { ...field, label: e.target.value };
                    setFormData(prev => ({ ...prev, requiredFields: newFields }));
                  }}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>

              <div className="col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={field.isRequired}
                    onChange={(e) => {
                      const newFields = [...formData.requiredFields];
                      newFields[index] = { ...field, isRequired: e.target.checked };
                      setFormData(prev => ({ ...prev, requiredFields: newFields }));
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Required</span>
                </label>
              </div>

              <div className="col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={field.supportsAutofill}
                    onChange={(e) => {
                      const newFields = [...formData.requiredFields];
                      newFields[index] = { 
                        ...field, 
                        supportsAutofill: e.target.checked,
                        // Clear API endpoint if autofill is disabled
                        autofillApiEndpoint: e.target.checked ? field.autofillApiEndpoint : ''
                      };
                      setFormData(prev => ({ ...prev, requiredFields: newFields }));
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Supports Autofill</span>
                </label>
              </div>

              {field.supportsAutofill && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Autofill API Endpoint
                  </label>
                  <input
                    type="text"
                    value={field.autofillApiEndpoint || ''}
                    onChange={(e) => {
                      const newFields = [...formData.requiredFields];
                      newFields[index] = { ...field, autofillApiEndpoint: e.target.value };
                      setFormData(prev => ({ ...prev, requiredFields: newFields }));
                    }}
                    placeholder="https://api.example.com/autofill"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required={field.supportsAutofill}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the API endpoint that will be used for autofilling this field
                  </p>
                </div>
              )}

              <div className="col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => handleRequiredFieldRemove(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <FaTrash className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Verification Types */}
        <div className="pt-8 bg-white rounded-lg shadow p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">Verification Types</h3>
            <p className="mt-1 text-sm text-gray-500">
              Select the verification types required for this product
            </p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
            {verificationTypes.map((type) => (
              <div key={type._id} className="flex items-center">
              <input
                type="checkbox"
                  id={`verification-type-${type._id}`}
                  checked={formData.verificationTypes.includes(type._id)}
                  onChange={(e) => {
                    const newVerificationTypes = e.target.checked
                      ? [...formData.verificationTypes, type._id]
                      : formData.verificationTypes.filter(id => id !== type._id);
                    setFormData(prev => ({
                  ...prev,
                      verificationTypes: newVerificationTypes
                    }));
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor={`verification-type-${type._id}`}
                  className="ml-3 text-sm text-gray-700"
                >
                  {type.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Document Requirements */}
        <div className="pt-8 bg-white rounded-lg shadow p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">Document Requirements</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add required documents for this product
            </p>
          </div>

          {formData.documentRequirements.map((docReq, index) => (
            <div key={index} className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 border p-4 rounded-md">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Document Name
                </label>
                <select
                  value={docReq.name}
                  onChange={e => {
                    const selectedField = uploadFields.find(f => f.name === e.target.value);
                    const newDocReqs = [...formData.documentRequirements];
                    newDocReqs[index] = {
                      ...docReq,
                      name: selectedField?.name || '',
                      description: selectedField?.description || ''
                    };
                    setFormData(prev => ({ ...prev, documentRequirements: newDocReqs }));
                  }}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Document Name</option>
                  {uploadFields.map(field => (
                    <option key={field._id} value={field.name}>{field.label}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <input
                  type="text"
                  value={docReq.description}
                  readOnly
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Template
                </label>
                <PasteUploadArea
                  onFileSelect={(file) => handleTemplateUpload(index, file)}
                  accept=".docx"
                  maxSize={10 * 1024 * 1024}
                  placeholder="Upload a Word document template (.docx)"
                  uploadedUrl={docReq.template?.fileUrl}
                />
                {docReq.template?.fileName && (
                  <p className="mt-1 text-sm text-gray-500">
                    Current template: {docReq.template.fileName}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={docReq.generateOnlyIfMissing}
                    onChange={(e) => {
                      const newDocReqs = [...formData.documentRequirements];
                      newDocReqs[index] = { ...docReq, generateOnlyIfMissing: e.target.checked };
                      setFormData(prev => ({ ...prev, documentRequirements: newDocReqs }));
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Generate only if master data document is missing
                  </span>
                </label>
              </div>

              {/* Notification Settings */}
              <div className="col-span-2 border-t pt-4 mt-4">
                <h3 className="text-md font-medium mb-4">Notification Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2">On Generate</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Email Notifications */}
                      <div>
                        <label className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={docReq.notifications?.onGenerate?.email?.enabled}
                            onChange={(e) => {
                              const newDocReqs = [...formData.documentRequirements];
                              newDocReqs[index] = {
                                ...docReq,
                                notifications: {
                                  ...docReq.notifications,
                                  onGenerate: {
                                    ...docReq.notifications?.onGenerate,
                                    email: {
                    enabled: e.target.checked
                  }
                                  }
                                }
                              };
                              setFormData(prev => ({ ...prev, documentRequirements: newDocReqs }));
                            }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                            Email Notifications
              </span>
            </label>
                      </div>

                      {/* SMS Notifications */}
                      <div>
                        <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                            checked={docReq.notifications?.onGenerate?.sms?.enabled}
                            onChange={(e) => {
                              const newDocReqs = [...formData.documentRequirements];
                              newDocReqs[index] = {
                                ...docReq,
                                notifications: {
                                  ...docReq.notifications,
                                  onGenerate: {
                                    ...docReq.notifications?.onGenerate,
                                    sms: {
                                      enabled: e.target.checked
                                    }
                                  }
                                }
                              };
                              setFormData(prev => ({ ...prev, documentRequirements: newDocReqs }));
                            }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                            SMS Notifications
                  </span>
                </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => handleDocumentRequirementRemove(index)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Remove Document
                </button>
              </div>
            </div>
          ))}

          <div className="mt-6">
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  documentRequirements: [
                    ...prev.documentRequirements,
                    {
                      name: '',
                      description: '',
                      masterDataSource: null,
                      template: null,
                      generateOnlyIfMissing: false,
                      notifications: {
                        onGenerate: {
                          email: { enabled: false },
                          sms: { enabled: false }
                        }
                      }
                    }
                  ]
                }));
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Document Requirement
            </button>
          </div>
        </div>

        {/* Associated Forms and Documents */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Forms</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Associated Forms
              </label>
              <select
                multiple
                value={formData.associatedForms}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  associatedForms: Array.from(e.target.selectedOptions, option => option.value)
                }))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                {forms && forms.map(form => (
                  <option key={form._id} value={form._id}>
                    {form.name}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-5">
          <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {submitting ? 'Saving...' : isEditMode ? 'Update Product' : 'Create Product'}
          </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm; 